import {FC, useState} from 'react'
import {useIntl} from 'react-intl'
import {IOfferModel} from '../cors/_models'
import {activateOffer, pauseOffer} from '../cors/_requests'
import SharedData from '../../common/SharedData'
import NormalMessage from '../../common/NormalMessage'
import AlertMessage from '../../common/alert/AlertMsg'
import OfferTableRow from './OfferTableRow'

/**
 * Renders the offers table + handles row-level pause/activate. Edit
 * action delegates to the parent (which opens the drawer); state lives
 * with the parent so the drawer + table stay in sync.
 *
 * Optimistic refresh — every successful mutation triggers `onChanged`,
 * which the parent uses to refetch the page (and the SummaryStrip).
 */

type IProps = {
  offers: IOfferModel[]
  onEdit: (offer: IOfferModel) => void
  onChanged: () => void | Promise<void>
}

const OffersTable: FC<IProps> = ({offers, onEdit, onChanged}) => {
  const {formatMessage} = useIntl()
  const {currentUser, language} = SharedData()
  const [busyId, setBusyId] = useState<number | null>(null)
  const [alert, setAlert] = useState<{
    title: string
    message: string
    status: boolean
    variant?: 'success' | 'danger' | 'warning'
  }>({title: '', message: '', status: false})

  const setMsg = (
    message: string,
    title: string,
    status = true,
    variant?: 'success' | 'danger' | 'warning',
  ) => setAlert({message, title, status, variant})

  const wrap = async (offer: IOfferModel, fn: () => Promise<void>) => {
    setBusyId(offer.product_supplier_id)
    try {
      await fn()
      await onChanged()
    } catch {
      setMsg(
        formatMessage({id: 'something_wrong_desc'}),
        formatMessage({id: 'something_wrong_title'}),
        false,
      )
    } finally {
      setBusyId(null)
    }
  }

  const handlePause = (offer: IOfferModel) =>
    wrap(offer, async () => {
      await pauseOffer(offer.product_supplier_id, currentUser?.token, language)
      // Pausing hides the offer from customers — surface it as a warning
      // (yellow), not a celebratory success toast.
      setMsg(
        formatMessage({id: 'offers.toast.paused'}),
        formatMessage({id: 'warning_general_title'}),
        true,
        'warning',
      )
    })

  const handleActivate = (offer: IOfferModel) =>
    wrap(offer, async () => {
      await activateOffer(offer.product_supplier_id, currentUser?.token, language)
      setMsg(
        formatMessage({id: 'offers.toast.activated'}),
        formatMessage({id: 'success_general_title'}),
      )
    })

  if (offers.length === 0) {
    return <NormalMessage msg={formatMessage({id: 'no_data'})} />
  }

  return (
    <>
      <div className="hsb-of-table-scroll">
        <table className="hsb-of-table">
          <thead>
            <tr>
              <th>★ {formatMessage({id: 'offers.col.winning'})}</th>
              <th>{formatMessage({id: 'offers.col.product'})}</th>
              <th>{formatMessage({id: 'offers.col.barcode'})}</th>
              <th>{formatMessage({id: 'offers.col.price'})}</th>
              <th>{formatMessage({id: 'offers.col.stock'})}</th>
              <th>{formatMessage({id: 'offers.col.delivery'})}</th>
              <th>{formatMessage({id: 'offers.col.status'})}</th>
              <th className="hsb-of-th-end">
                {formatMessage({id: 'offers.col.actions'})}
              </th>
            </tr>
          </thead>
          <tbody>
            {offers.map((o) => (
              <OfferTableRow
                key={o.product_supplier_id}
                offer={o}
                busy={busyId === o.product_supplier_id}
                onEdit={onEdit}
                onPause={handlePause}
                onActivate={handleActivate}
              />
            ))}
          </tbody>
        </table>
      </div>

      {alert.message ? (
        <AlertMessage
          onClose={() => setAlert({title: '', message: '', status: false})}
          message={alert.message}
          title={alert.title}
          status={alert.status}
          variant={alert.variant}
        />
      ) : null}
    </>
  )
}

export default OffersTable
